import React, { useMemo } from 'react';

/**
 * 텍스트에서 검색어를 하이라이트하는 컴포넌트
 * @param {string} text - 원본 텍스트
 * @param {string} searchTerm - 검색어
 * @param {string} className - 추가 CSS 클래스
 * @param {boolean} isTitle - 제목인지 여부 (더 강한 하이라이트)
 */
const HighlightedText = ({ text, searchTerm, className = '', isTitle = false }) => {
  const highlightedContent = useMemo(() => {
    // 텍스트나 검색어가 없으면 원본 반환
    if (!text || !searchTerm || searchTerm.trim() === '') {
      return <span className={className}>{text}</span>;
    }

    // 검색어 정리 (공백 제거, 특수문자 이스케이프)
    const cleanSearchTerm = searchTerm.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // 빈 검색어 처리
    if (!cleanSearchTerm) {
      return <span className={className}>{text}</span>;
    }

    try {
      // 대소문자 무관 검색을 위한 정규식
      const regex = new RegExp(`(${cleanSearchTerm})`, 'gi');
      
      // 텍스트를 검색어 기준으로 분할
      const parts = text.split(regex);
      
      // 각 부분을 처리하여 하이라이트 적용
      return (
        <span className={className}>
          {parts.map((part, index) => {
            // 검색어와 일치하는 부분인지 확인 (대소문자 무관)
            const isMatch = part && part.toLowerCase() === cleanSearchTerm.toLowerCase();
            
            if (isMatch) {
              // 제목인 경우 더 강한 하이라이트
              if (isTitle) {
                return (
                  <mark
                    key={index}
                    className="bg-yellow-300 text-gray-900 px-0.5 rounded font-semibold animate-highlight"
                    style={{
                      boxShadow: '0 0 0 2px rgba(251, 191, 36, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {part}
                  </mark>
                );
              }
              
              // 일반 텍스트 하이라이트
              return (
                <mark
                  key={index}
                  className="bg-yellow-200 text-gray-900 px-0.5 rounded"
                  style={{
                    transition: 'all 0.3s ease'
                  }}
                >
                  {part}
                </mark>
              );
            }
            
            // 일치하지 않는 부분은 그대로 반환
            return <span key={index}>{part}</span>;
          })}
        </span>
      );
    } catch (error) {
      // 정규식 오류 발생 시 원본 반환
      console.error('Highlight error:', error);
      return <span className={className}>{text}</span>;
    }
  }, [text, searchTerm, className, isTitle]);

  return highlightedContent;
};

/**
 * 긴 텍스트에서 검색어 주변만 표시하는 컴포넌트
 */
export const HighlightedTextWithContext = ({ text, searchTerm, maxLength = 150, className = '' }) => {
  const processedContent = useMemo(() => {
    if (!text || !searchTerm || searchTerm.trim() === '') {
      // 검색어가 없으면 텍스트 앞부분만 표시
      const truncated = text && text.length > maxLength 
        ? text.slice(0, maxLength) + '...'
        : text;
      return <span className={className}>{truncated}</span>;
    }

    const cleanSearchTerm = searchTerm.trim().toLowerCase();
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf(cleanSearchTerm);
    
    // 검색어를 찾지 못한 경우
    if (index === -1) {
      const truncated = text.length > maxLength 
        ? text.slice(0, maxLength) + '...'
        : text;
      return <span className={className}>{truncated}</span>;
    }
    
    // 검색어 주변 컨텍스트 계산
    const contextBefore = 40;
    const contextAfter = maxLength - contextBefore - cleanSearchTerm.length;
    
    const start = Math.max(0, index - contextBefore);
    const end = Math.min(text.length, index + cleanSearchTerm.length + contextAfter);
    
    const excerpt = text.slice(start, end);
    const prefix = start > 0 ? '...' : '';
    const suffix = end < text.length ? '...' : '';
    
    return (
      <span className={className}>
        {prefix}
        <HighlightedText text={excerpt} searchTerm={searchTerm} />
        {suffix}
      </span>
    );
  }, [text, searchTerm, maxLength, className]);

  return processedContent;
};

/**
 * 여러 검색어를 다른 색상으로 하이라이트하는 컴포넌트
 */
export const MultiHighlightedText = ({ text, searchTerms = [], className = '' }) => {
  const highlightedContent = useMemo(() => {
    if (!text || !searchTerms || searchTerms.length === 0) {
      return <span className={className}>{text}</span>;
    }

    // 색상 팔레트
    const colors = [
      'bg-yellow-200',
      'bg-blue-200',
      'bg-green-200',
      'bg-pink-200',
      'bg-purple-200'
    ];

    let processedText = text;
    searchTerms.forEach((term, index) => {
      if (term && term.trim()) {
        const color = colors[index % colors.length];
        const regex = new RegExp(`(${term.trim()})`, 'gi');
        processedText = processedText.replace(regex, `<mark class="${color} px-0.5 rounded">$1</mark>`);
      }
    });

    return (
      <span 
        className={className}
        dangerouslySetInnerHTML={{ __html: processedText }}
      />
    );
  }, [text, searchTerms, className]);

  return highlightedContent;
};

export default HighlightedText;